import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  CaretLeft,
  CaretRight,
  Info,
  Lightbulb,
  Plus,
  Sparkle,
  Users,
  Warning,
} from '@phosphor-icons/react';
import { addDays } from 'date-fns';
import * as React from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';

import { CurrentTimeIndicator } from './CurrentTimeIndicator';
import { DayHeader } from './DayHeader';
import { FilterChipRow } from './FilterChipRow';
import { StaffDayColumn } from './StaffRow';
import { TimeAxis } from './TimeAxis';
import type {
  AIInsight,
  Appointment,
  TimeSlot,
  WeeklyCalendarProps,
} from './types';
import {
  applyFilters,
  buildTimeSlots,
  detectConflicts,
  fmtTime,
  getWeekDays,
  isSameDay,
  moveAppointment,
  parseHHmm,
  snapToSlot,
} from './utils';

const SLOT_HEIGHT = 36;
const STAFF_COL_MIN = 168;
const TIME_AXIS_WIDTH = 64;

/**
 * WeeklyCalendar — tablet-first 7-day × N-staff grid (B1.4).
 *
 * This component is intentionally presentational: all data comes in through
 * props. Side sheets for appointment details and "new appointment" flows are
 * owned by the smart wrapper (`features/appointments/WeeklyCalendarContainer`)
 * via the `onOpenDetails` / `onCreateAppointment` callbacks — the calendar
 * never renders its own modal/sheet.
 */
export function WeeklyCalendar(props: WeeklyCalendarProps): JSX.Element {
  const {
    weekStart,
    staff,
    appointments,
    workingHours,
    onCreateAppointment,
    onUpdateAppointment,
    onOpenDetails,
    activeFilters = [],
    availableFilters = [],
    onFilterChange,
    visibleStaffIds,
    onStaffVisibilityChange,
    onWeekChange,
    onJumpToToday,
    onAIAssist,
    aiInsights = [],
    labels,
    brandOverride,
    dataTestId,
    isLoading,
    error,
  } = props;

  const slots = React.useMemo(
    () => buildTimeSlots(workingHours.start, workingHours.end, 30),
    [workingHours.start, workingHours.end],
  );
  const days = React.useMemo(() => getWeekDays(weekStart), [weekStart]);

  const visibleStaff = React.useMemo(() => {
    if (!visibleStaffIds || visibleStaffIds.length === 0) {
      return staff.filter((s) => s.isActive);
    }
    const set = new Set(visibleStaffIds);
    return staff.filter((s) => s.isActive && set.has(s.id));
  }, [staff, visibleStaffIds]);

  const conflicts = React.useMemo(() => detectConflicts(appointments), [appointments]);
  const filteredIds = React.useMemo(
    () => applyFilters(appointments, activeFilters),
    [appointments, activeFilters],
  );

  const [activeDrag, setActiveDrag] = React.useState<Appointment | null>(null);
  const [staffMenuOpen, setStaffMenuOpen] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const shake = (id: string) => {
    const el = document.querySelector<HTMLElement>(`[data-appointment-id="${id}"]`);
    if (!el) return;
    el.classList.add('animate-biz-shake');
    window.setTimeout(() => el.classList.remove('animate-biz-shake'), 220);
  };

  const handleDragStart = (e: DragStartEvent) => {
    const a = e.active.data.current?.appointment as Appointment | undefined;
    if (a) setActiveDrag(a);
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveDrag(null);
    const overData = e.over?.data.current as
      | { date: Date; staffId: string; startTime: string }
      | undefined;
    const a = e.active.data.current?.appointment as Appointment | undefined;
    if (!overData || !a) return;

    const moved = moveAppointment(a, overData.date, overData.startTime, overData.staffId);

    if (moved.startTime < new Date()) {
      shake(a.id);
      toast.error(labels.conflictWarning);
      return;
    }
    const others = appointments.filter(
      (x) =>
        x.id !== a.id &&
        x.staffId === moved.staffId &&
        x.status !== 'cancelled' &&
        x.status !== 'no_show' &&
        isSameDay(x.startTime, moved.startTime),
    );
    const overlap = others.some(
      (x) => moved.startTime < x.endTime && moved.endTime > x.startTime,
    );
    if (overlap) {
      shake(a.id);
      toast.error(labels.conflictWarning);
      return;
    }
    try {
      await onUpdateAppointment?.(a.id, {
        startTime: moved.startTime,
        endTime: moved.endTime,
        staffId: moved.staffId,
      });
    } catch {
      toast.error(labels.errorTitle);
    }
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest('input, textarea, select, [contenteditable]')) {
        return;
      }
      if (e.key === 'ArrowLeft' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onWeekChange?.(addDays(weekStart, -7));
      } else if (e.key === 'ArrowRight' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onWeekChange?.(addDays(weekStart, 7));
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        const today = new Date();
        const startMin = parseHHmm(workingHours.start);
        const slot: TimeSlot = {
          date: snapToSlot(today),
          staffId: visibleStaff[0]?.id ?? '',
          startTime: fmtTime(snapToSlot(today)) || `${startMin.h}:${startMin.m}`,
        };
        onCreateAppointment?.(slot);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [weekStart, onWeekChange, onCreateAppointment, visibleStaff, workingHours.start]);

  const styleVars: React.CSSProperties = brandOverride
    ? ({
        ...(brandOverride.primaryHue
          ? { ['--biz-primary' as string]: brandOverride.primaryHue }
          : {}),
        ...(brandOverride.accentHue
          ? { ['--biz-accent' as string]: brandOverride.accentHue }
          : {}),
      } as React.CSSProperties)
    : {};

  const weekLabel = React.useMemo(() => {
    const last = days[6];
    return `${labels.weekOf} ${weekStart.getDate()} ${labels.monthNames[weekStart.getMonth()]} – ${last.getDate()} ${labels.monthNames[last.getMonth()]} ${last.getFullYear()}`;
  }, [weekStart, days, labels]);

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-biz-bg text-biz-foreground"
      style={styleVars}
      data-testid={dataTestId}
    >
      <div className="flex flex-col gap-3 px-6 pt-5 pb-3 border-b border-biz-border bg-biz-bg sticky top-0 z-30">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl font-bold">{labels.title}</h1>
            <div className="flex items-center gap-1">
              <NavBtn
                ariaLabel={labels.prevWeek}
                onClick={() => onWeekChange?.(addDays(weekStart, -7))}
              >
                <CaretLeft size={20} weight="regular" />
              </NavBtn>
              <button
                type="button"
                onClick={() => onJumpToToday?.()}
                className="h-11 min-w-[88px] px-4 rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-sm font-medium text-biz-foreground hover:bg-biz-muted transition-colors biz-focus-ring"
              >
                {labels.today}
              </button>
              <NavBtn
                ariaLabel={labels.nextWeek}
                onClick={() => onWeekChange?.(addDays(weekStart, 7))}
              >
                <CaretRight size={20} weight="regular" />
              </NavBtn>
            </div>
            <div className="font-mono-num text-sm text-biz-foreground-muted hidden md:block">
              {weekLabel}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setStaffMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={staffMenuOpen}
                className="inline-flex items-center gap-2 h-11 px-3 rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-sm font-medium hover:bg-biz-muted transition-colors biz-focus-ring"
              >
                <Users size={16} weight="regular" />
                {labels.allStaff}
                <span className="font-mono-num rounded-full bg-biz-muted px-1.5 text-xs text-biz-foreground-muted">
                  {visibleStaff.length}/{staff.filter((s) => s.isActive).length}
                </span>
              </button>
              {staffMenuOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 top-full mt-1 w-64 z-40 rounded-biz-card border border-biz-border bg-biz-surface-elevated shadow-biz-lg p-2"
                  onMouseLeave={() => setStaffMenuOpen(false)}
                >
                  {staff
                    .filter((s) => s.isActive)
                    .map((s) => {
                      const checked = visibleStaff.some((v) => v.id === s.id);
                      return (
                        <label
                          key={s.id}
                          className="flex items-center gap-2 px-2 py-2 rounded-biz-btn hover:bg-biz-muted cursor-pointer min-h-[44px]"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const cur = visibleStaff.map((v) => v.id);
                              const next = e.target.checked
                                ? [...new Set([...cur, s.id])]
                                : cur.filter((id) => id !== s.id);
                              onStaffVisibilityChange?.(next);
                            }}
                            className="h-4 w-4 accent-[hsl(var(--biz-primary))]"
                          />
                          {s.avatarUrl && (
                            <img
                              src={s.avatarUrl}
                              alt=""
                              className="h-7 w-7 rounded-full object-cover"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{s.fullName}</div>
                            <div className="text-xs text-biz-foreground-muted truncate">
                              {s.role}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                </div>
              )}
            </div>

            {onAIAssist && (
              <button
                type="button"
                onClick={() => onAIAssist('calendar')}
                className="inline-flex items-center gap-2 h-11 px-3 rounded-biz-btn border border-biz-info/40 bg-biz-info-subtle text-sm font-medium text-biz-info hover:bg-biz-info/15 transition-colors biz-focus-ring"
              >
                <Sparkle size={16} weight="regular" />
                {labels.aiAssistant}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                const slot: TimeSlot = {
                  date: days[0],
                  staffId: visibleStaff[0]?.id ?? '',
                  startTime: workingHours.start,
                };
                onCreateAppointment?.(slot);
              }}
              className="inline-flex items-center gap-2 h-11 px-4 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold text-sm hover:bg-biz-primary-hover transition-colors biz-focus-ring shadow-biz-sm"
            >
              <Plus size={18} weight="regular" />
              {labels.newAppointment}
            </button>
          </div>
        </div>

        {availableFilters.length > 0 && (
          <FilterChipRow
            available={availableFilters}
            active={activeFilters}
            onChange={(next) => onFilterChange?.(next)}
            allLabel={labels.filterAll}
          />
        )}

        <AIInsightsBanner insights={aiInsights} />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {error ? (
          <ErrorState message={error.message} title={labels.errorTitle} />
        ) : isLoading ? (
          <LoadingState label={labels.loading} />
        ) : visibleStaff.length === 0 ? (
          <EmptyState message={labels.noAppointments} />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <CalendarGrid
              days={days}
              staff={visibleStaff}
              slots={slots}
              workingHours={workingHours}
              appointments={appointments}
              conflicts={conflicts}
              filteredIds={filteredIds}
              labels={labels}
              onSelectAppointment={(a) => onOpenDetails?.(a)}
              onCreateAppointment={(slot) => onCreateAppointment?.(slot)}
            />
            <DragOverlay dropAnimation={null}>
              {activeDrag && (
                <div className="rounded-biz-card shadow-biz-drag border-l-4 border-l-biz-primary bg-biz-surface-elevated px-3 py-2 w-48">
                  <div className="text-sm font-semibold">{activeDrag.petName}</div>
                  <div className="font-mono-num text-xs text-biz-foreground-muted">
                    {fmtTime(activeDrag.startTime)} – {fmtTime(activeDrag.endTime)}
                  </div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </div>
  );
}

function NavBtn({
  children,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="inline-flex items-center justify-center h-11 w-11 rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-biz-foreground hover:bg-biz-muted transition-colors biz-focus-ring"
    >
      {children}
    </button>
  );
}

interface CalendarGridProps {
  days: Date[];
  staff: WeeklyCalendarProps['staff'];
  slots: string[];
  workingHours: WeeklyCalendarProps['workingHours'];
  appointments: Appointment[];
  conflicts: Set<string>;
  filteredIds: Set<string>;
  labels: WeeklyCalendarProps['labels'];
  onSelectAppointment?: (a: Appointment) => void;
  onCreateAppointment?: (s: TimeSlot) => void;
}

function CalendarGrid({
  days,
  staff,
  slots,
  workingHours,
  appointments,
  conflicts,
  filteredIds,
  labels,
  onSelectAppointment,
  onCreateAppointment,
}: CalendarGridProps): JSX.Element {
  const today = new Date();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [colWidth, setColWidth] = React.useState(STAFF_COL_MIN);

  React.useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      const w = containerRef.current?.clientWidth ?? 0;
      const avail = w - TIME_AXIS_WIDTH;
      const totalCols = days.length * staff.length;
      const ideal = Math.floor(avail / totalCols);
      setColWidth(Math.max(STAFF_COL_MIN, ideal));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [days.length, staff.length]);

  const todayIndex = days.findIndex((d) => isSameDay(d, today));
  const dayWidth = staff.length * colWidth;

  return (
    <div
      ref={containerRef}
      className="h-full overflow-auto biz-scrollbar"
      role="grid"
      aria-label={labels.title}
    >
      <div style={{ width: TIME_AXIS_WIDTH + days.length * dayWidth, minWidth: '100%' }}>
        <div
          className="sticky top-0 z-20 bg-biz-bg border-b border-biz-border grid"
          style={{
            gridTemplateColumns: `${TIME_AXIS_WIDTH}px repeat(${days.length}, ${dayWidth}px)`,
          }}
        >
          <div className="border-r border-biz-border bg-biz-surface" />
          {days.map((d, i) => (
            <div key={i} className="border-r border-biz-border last:border-r-0">
              <DayHeader date={d} labels={labels} isToday={isSameDay(d, today)} />
              <div
                className="grid border-t border-biz-border bg-biz-surface"
                style={{ gridTemplateColumns: `repeat(${staff.length}, ${colWidth}px)` }}
              >
                {staff.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-2 px-2 py-1.5 border-r border-biz-border last:border-r-0 min-w-0"
                    title={s.fullName}
                  >
                    {s.avatarUrl && (
                      <img
                        src={s.avatarUrl}
                        alt=""
                        className="h-6 w-6 rounded-full object-cover shrink-0"
                      />
                    )}
                    <span className="text-[11px] font-medium text-biz-foreground truncate">
                      {s.fullName.split(' ')[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className="relative grid"
          style={{
            gridTemplateColumns: `${TIME_AXIS_WIDTH}px repeat(${days.length}, ${dayWidth}px)`,
          }}
        >
          <TimeAxis slots={slots} slotHeightPx={SLOT_HEIGHT} />
          {days.map((d, i) => {
            const isTodayCol = isSameDay(d, today);
            return (
              <div
                key={i}
                className="grid relative border-r border-biz-border last:border-r-0"
                style={{ gridTemplateColumns: `repeat(${staff.length}, ${colWidth}px)` }}
              >
                {staff.map((s) => (
                  <StaffDayColumn
                    key={s.id}
                    staff={s}
                    date={d}
                    slots={slots}
                    slotHeightPx={SLOT_HEIGHT}
                    workingStart={workingHours.start}
                    appointments={appointments}
                    conflicts={conflicts}
                    filteredIds={filteredIds}
                    labels={labels}
                    onSelectAppointment={onSelectAppointment}
                    onCreateAppointment={onCreateAppointment}
                    isToday={isTodayCol}
                  />
                ))}
              </div>
            );
          })}

          {todayIndex >= 0 && (
            <CurrentTimeIndicator
              workingStart={workingHours.start}
              workingEnd={workingHours.end}
              slotHeightPx={SLOT_HEIGHT}
              leftOffsetPx={TIME_AXIS_WIDTH + todayIndex * dayWidth}
              containerWidthPx={dayWidth}
              label={labels.now}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function AIInsightsBanner({ insights }: { insights: AIInsight[] }): JSX.Element | null {
  if (!insights.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {insights.map((i) => {
        const Icon = i.type === 'warning' ? Warning : i.type === 'info' ? Info : Lightbulb;
        const tint =
          i.type === 'warning'
            ? 'border-biz-warning-border bg-biz-warning-subtle text-biz-foreground'
            : 'border-biz-info-border bg-biz-info-subtle text-biz-foreground';
        return (
          <div
            key={i.id}
            className={cn(
              'flex items-center gap-2 rounded-biz-card border px-3 py-2 text-sm',
              tint,
            )}
            role="status"
          >
            <Icon size={16} weight="regular" />
            <span className="flex-1">{i.message}</span>
            {i.action && (
              <button
                type="button"
                onClick={i.action.onClick}
                className="h-9 px-3 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-xs font-semibold hover:bg-biz-primary-hover transition-colors biz-focus-ring"
              >
                {i.action.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LoadingState({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full text-biz-foreground-muted">
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rounded-full bg-biz-primary animate-pulse" />
        <span className="text-sm font-medium">{label}</span>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full text-biz-foreground-muted text-sm">
      {message}
    </div>
  );
}

function ErrorState({ title, message }: { title: string; message: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 text-biz-danger">
      <div className="font-display font-semibold">{title}</div>
      <div className="text-sm text-biz-foreground-muted">{message}</div>
    </div>
  );
}
